/** Matches BoxBuilderContext hideWildgrainAddToCartBlockingOverlay — call when Member Deals is visible */
function removeWildgrainAtcBlockingOverlay() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('wildgrain-add-to-cart-blocking-overlay');
  if (el) el.remove();
  document.body.classList.remove('wildgrain-add-to-cart-blocking');
}

/** Same minus / trash icons as bundle ProductCard (qty 1 → remove) */
const WG_ADDON_MINUS_SVG =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4"/></svg>';
const WG_ADDON_IMAGE_PLACEHOLDER =
  '<div class="checkout-addon-product__placeholder" aria-hidden="true"></div>';
const WG_ADDON_TRASH_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>';

/** Same ticket icon as custom-box-preview-modal footer discount strip */
const WG_CHECKOUT_ADDON_DISCOUNT_TICKET_SVG =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.5 8.65467L4.42267 6.732M11.5773 13.7693L13.5 11.8467M10.0773 3.232L6.92267 6.38667M13.2333 5.26667L10.7333 2.76667L2.76667 10.7333L5.26667 13.2333L13.2333 5.26667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Wait until the drawer panel finishes sliding in (theme .drawer__inner uses ~0.6s transform).
 * Lets the user see Member Deals animate instead of a dim layer popping off mid-motion.
 */
function waitForMemberDealsDrawerEntrance(drawerEl) {
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) {
    return Promise.resolve();
  }
  const inner = drawerEl && drawerEl.querySelector('.drawer__inner');
  if (!inner) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      inner.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (e) => {
      if (e.target !== inner || e.propertyName !== 'transform') return;
      done();
    };
    inner.addEventListener('transitionend', onEnd);
    // Match checkout-addons.css .drawer__inner (~0.55s) + buffer
    setTimeout(done, 620);
  });
}

class CheckoutAddonsDrawer extends HTMLElement {
  constructor() {
    super();
    this.productsContainer = this.querySelector('[data-products-container]');
    this.checkoutButton = this.querySelector('[data-checkout-button]');
    this.checkoutButtonLabel = this.querySelector('[data-checkout-button-label]');
    this.footerSummary = this.querySelector('[data-checkout-addons-footer-summary]');
    this.loadingSpinner = this.querySelector('.checkout-addons-drawer__loading');
    this.filterContainer = this.querySelector('[data-filter-container]');
    this.quantities = {};
    this.allProducts = [];
    this.updatingVariants = new Set();
    this.activeTab = 'all';
    this.activeFilter = 'Deals';
    this.searchTerm = '';
    this._bundleCache = {};

    this.checkoutButton?.addEventListener('click', () => this.proceedToCheckout());
  }

  connectedCallback() {
    document.addEventListener('checkout-addons:open', (e) => this.open(e.detail?.products, e.detail?.cart));
  }

  logStatsigEvent(name, value, metadata) {
    try {
      const client = window.statsigClient;
      if (client && typeof client.logEvent === 'function') {
        client.logEvent(name, value || '', metadata || {});
      }
    } catch (e) {}
  }

  flushStatsigEvents() {
    try {
      const client = window.statsigClient;
      if (client && typeof client.flush === 'function') client.flush();
    } catch (e) {}
  }

  getProductByVariantId(variantId) {
    const vid = String(variantId);
    return (this.allProducts || []).find(p =>
      (p.variants || []).some(v => String(v.id) === vid)
    );
  }

  logAddonQuantityEvent(variantId, previousQty, newQty, source) {
    const product = this.getProductByVariantId(variantId);
    this.logStatsigEvent(
      newQty === 0 ? 'funnel_addons_item_removed' : 'funnel_addons_quantity_changed',
      product?.title || product?.handle || String(variantId),
      {
        variant_id: String(variantId),
        product_id: String(product?.id || ''),
        handle: product?.handle || '',
        previous_qty: String(previousQty),
        new_qty: String(newQty),
        direction: newQty > previousQty ? 'increase' : 'decrease',
        source
      }
    );
  }

  async open(preloadedProducts = null, preloadedCart = null) {
    if (this.getCookie('noAddonModal')) {
      removeWildgrainAtcBlockingOverlay();
      this.proceedToCheckout();
      return;
    }

    // Member Deals stacks above box selection (z-index). Reveal the slide-in before tearing down preview.
    this.removeAttribute('hidden');
    this.setAttribute('open', '');

    // Prevent body scroll without layout shift by preserving scrollbar space
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('checkout-addons-drawer-open');
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Also lock html element
    document.documentElement.style.overflow = 'hidden';

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        this.setAttribute('active', '');

        // Ensure scroll lock is still applied after animation starts
        const scrollbarWidthInner = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('checkout-addons-drawer-open');
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidthInner > 0) {
          document.body.style.paddingRight = `${scrollbarWidthInner}px`;
        }

        requestAnimationFrame(() => {
          // ATC overlay sits above all UI — remove it now so the drawer animation is visible
          removeWildgrainAtcBlockingOverlay();
          resolve();
        });
      });
    });

    const loadContent = (async () => {
      try {
        // Always fetch fresh /cart.js for accurate monetary totals.
        // Preloaded carts passed via the event may carry stale total_price / items_subtotal_price
        // (e.g. from a cart snapshot taken before the bundle was added).
        await this.syncWithCart();

        if (!this.productsLoaded) {
          if (preloadedProducts && Array.isArray(preloadedProducts)) {
            this.setProducts(preloadedProducts);
            this.productsLoaded = true;
          } else {
            await this.loadProducts();
            this.productsLoaded = true;
          }
        } else {
          // Re-apply the current filter so products added to cart from
          // elsewhere appear in the DOM (filterProducts includes in-cart items).
          if (this.searchTerm) {
            this.applySearch(this.searchTerm);
          } else {
            this.filterProducts(this.activeFilter);
          }
        }
      } catch (err) {
        console.error('Checkout addons drawer: failed to load content', err);
      }
    })();

    await waitForMemberDealsDrawerEntrance(this);

    const previewModal = document.querySelector('custom-box-preview-modal');
    if (previewModal && typeof previewModal.close === 'function') {
      previewModal.close(true);
    }

    await loadContent;

    const cart = this._lastCart;
    this.logStatsigEvent('funnel_addons_drawer_opened', 'member_deals', {
      products_loaded: String((this.allProducts || []).length),
      cart_item_count: String((cart && cart.item_count) || 0),
      cart_total: String(cart && cart.total_price ? (cart.total_price / 100) : 0),
      preloaded: preloadedProducts ? 'true' : 'false'
    });
  }

  close() {
    // Remove 'active' first to start the closing animation (overlay fade-out, drawer slide)
    this.removeAttribute('active');

    // Restore body scroll and fully hide the drawer after the transition completes
    const overlay = this.querySelector('.fixed-overlay');
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      this.removeAttribute('open');
      this.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('checkout-addons-drawer-open');
      document.documentElement.style.overflow = '';
    };

    if (overlay) {
      overlay.addEventListener('transitionend', cleanup, { once: true });
      // Safety fallback — if transitionend never fires (e.g. no transition applied),
      // ensure cleanup still runs so the page is never left frozen.
      setTimeout(cleanup, 800);
    } else {
      setTimeout(cleanup, 600);
    }
  }

  async syncWithCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.syncWithCartData(cart);
      return cart;
    } catch (error) {
      console.error('Error syncing with cart:', error);
      return null;
    }
  }

  syncWithCartData(cart) {
    if (!cart) {
      this.quantities = {};
      this.cartItemsSKUs = {};
      this.cartItemsProductTypes = {};
      this._lastCart = null;
      this.updateFooterSummary(null);
      return;
    }

    if (!cart.items) {
      cart.items = [];
    }

    this.quantities = {};
    this.cartItemsSKUs = {};
    this.cartItemsProductTypes = {};

    cart.items.forEach(item => {
      this.quantities[item.variant_id] = item.quantity;
      this.cartItemsSKUs[item.variant_id] = item.sku || '';
      this.cartItemsProductTypes[item.variant_id] = item.product_type || '';
    });

    this._lastCart = cart;
    this.updateFooterSummary(cart);
  }

  escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Footer: discount strip first, then total row (Total: left, amount right).
   */
  updateFooterSummary(cart) {
    const el = this.footerSummary;
    if (!el) return;

    if (!cart) {
      el.innerHTML = '';
      return;
    }

    const toCents = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : NaN;
    };

    let totalCents = toCents(cart.total_price);
    let subtotalCents = toCents(cart.items_subtotal_price);
    if (!Number.isFinite(subtotalCents)) {
      subtotalCents = Number.isFinite(totalCents) ? totalCents : 0;
    }

    /**
     * Subscription / prepaid flows sometimes return total_price 0 while line items still
     * carry real amounts. Sum line final prices when cart total is zero but items exist.
     */
    const sumLineFinalCents = (items) =>
      (items || []).reduce((sum, it) => {
        const lf = toCents(it.final_line_price);
        const lp = toCents(it.line_price);
        const line = Number.isFinite(lf) ? lf : Number.isFinite(lp) ? lp : 0;
        return sum + line;
      }, 0);

    if ((!Number.isFinite(totalCents) || totalCents === 0) && Array.isArray(cart.items) && cart.items.length > 0) {
      const fallback = sumLineFinalCents(cart.items);
      if (fallback > 0) {
        totalCents = fallback;
        if (!Number.isFinite(subtotalCents) || subtotalCents === 0) {
          subtotalCents = fallback;
        }
      }
    }

    if (!Number.isFinite(totalCents)) {
      el.innerHTML = '';
      return;
    }

    const discountCents = Number.isFinite(toCents(cart.total_discount)) ? toCents(cart.total_discount) : 0;

    const finalStr = `$${(totalCents / 100).toFixed(2)}`;
    const compareStr = `$${(subtotalCents / 100).toFixed(2)}`;
    const showCompare = subtotalCents > totalCents;

    const compareBlock = showCompare
      ? `<span class="checkout-addons-drawer__footer-price-compare">
          <span class="visually-hidden">Original price&nbsp;</span>
          ${compareStr}
        </span>`
      : '';

    const totalRowClass = showCompare
      ? 'checkout-addons-drawer__footer-total-row'
      : 'checkout-addons-drawer__footer-total-row checkout-addons-drawer__footer-total-row--single';

    const totalRow = `
      <div class="${totalRowClass}">
        <span class="checkout-addons-drawer__footer-total-label">Total:</span>
        <div class="checkout-addons-drawer__footer-total-amounts">
          ${compareBlock}
          <span class="checkout-addons-drawer__footer-price-final">${finalStr}</span>
        </div>
      </div>
    `;

    const apps = cart.cart_level_discount_applications || [];
    let discountLabel = 'Discount applied';
    let displayDiscountCents = discountCents;
    if (apps.length > 0) {
      discountLabel = apps.map((a) => a.title).join(', ');
    }
    try {
      if (sessionStorage.getItem('wg_friend_offer') === 'true') {
        discountLabel = 'First Box Free';
        // Show the box's original price as the discount, not the cart-level $20 auto-promo.
        const boxItem = (cart.items || []).find((item) => item.sku && item.sku.startsWith('WGB_'));
        if (boxItem) {
          displayDiscountCents = toCents(boxItem.original_line_price) || toCents(boxItem.price) || displayDiscountCents;
        }
      }
    } catch (e) {}
    const discountLabelSafe = this.escapeHtml(discountLabel);
    const discountAmountStr = (displayDiscountCents / 100).toFixed(2);

    const discountStrip =
      displayDiscountCents > 0
        ? `
      <div class="checkout-addons-drawer__footer-discount-strip">
        <div class="checkout-addons-drawer__footer-discount-strip-start">
          ${WG_CHECKOUT_ADDON_DISCOUNT_TICKET_SVG}
          <span class="checkout-addons-drawer__footer-discount-name">${discountLabelSafe}</span>
        </div>
        <span class="checkout-addons-drawer__footer-discount-amount">-$${discountAmountStr}</span>
      </div>
    `
        : '';

    let lineItemSavingsCents = 0;
    if (Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        const orig = toCents(item.original_line_price);
        const final = toCents(item.final_line_price);
        if (Number.isFinite(orig) && Number.isFinite(final) && orig > final) {
          lineItemSavingsCents += orig - final;
        }
      });
    }

    let addonDealSavingsCents = 0;
    if (this.allProducts && this.allProducts.length) {
      this.allProducts.forEach((product) => {
        const v = product.variants?.[0];
        if (!v) return;
        const vid = v.id;
        const qty = this.quantities[vid] || this.quantities[String(vid)] || 0;
        if (qty <= 0) return;
        const cp = parseFloat(v.compare_at_price);
        const p = parseFloat(v.price);
        if (cp > p) {
          addonDealSavingsCents += Math.round((cp - p) * 100) * qty;
        }
      });
    }

    const totalSavingsCents = Math.max(lineItemSavingsCents, displayDiscountCents) + addonDealSavingsCents;

    let savingsMessage = `You're saving <strong>$${(totalSavingsCents / 100).toFixed(2)}</strong> with Member Deals`;
    try {
      if (sessionStorage.getItem('wg_friend_offer') === 'true') {
        savingsMessage = `Your first box is <strong>free</strong> — just pay shipping`;
      }
    } catch (e) {}

    const savingsStrip =
      totalSavingsCents > 0 || (function() { try { return sessionStorage.getItem('wg_friend_offer') === 'true'; } catch(e) { return false; } })()
        ? `<div class="checkout-addons-drawer__footer-savings">
            ${savingsMessage}
          </div>`
        : '';

    el.innerHTML = `
      <div class="checkout-addons-drawer__footer-checkout">
        ${discountStrip}
        ${savingsStrip}
        ${totalRow}
      </div>
    `;
  }

  refreshQuantityDisplays() {
    // First, reset all products to show "Add to box" button and remove in-cart borders
    const allControls = this.productsContainer.querySelectorAll('[data-variant-id]');
    const resetVariants = new Set();

    allControls.forEach(control => {
      const variantId = control.dataset.variantId;
      if (!resetVariants.has(variantId)) {
        resetVariants.add(variantId);
        // Reset to add button by default
        const variantControls = this.productsContainer.querySelectorAll(`[data-variant-id="${variantId}"]`);
        variantControls.forEach(ctrl => {
          if (ctrl.classList.contains('checkout-addon-product__quantity')) {
            ctrl.hidden = true;
            const input = ctrl.querySelector('input');
            if (input) input.value = 0;
            this.syncAddonDecreaseButton(ctrl, 0);
          } else if (ctrl.classList.contains('checkout-addon-product__add-btn')) {
            ctrl.hidden = false;
          }
        });

        // Remove in-cart border from product card
        const productCard = this.productsContainer.querySelector(`[data-product-id] [data-variant-id="${variantId}"]`)?.closest('[data-product-id]');
        if (productCard) {
          productCard.classList.remove('checkout-addon-product--in-cart');
        }
      }
    });

    // Then update based on cart quantities
    Object.keys(this.quantities).forEach(variantId => {
      const qty = this.quantities[variantId];
      if (qty > 0) {
        this.updateQuantityDisplay(variantId);
        // Show quantity controls instead of add button
        const controls = this.productsContainer.querySelectorAll(`[data-variant-id="${variantId}"]`);
        controls.forEach(control => {
          if (control.classList.contains('checkout-addon-product__quantity')) {
            control.hidden = false;
            const input = control.querySelector('input');
            if (input) input.value = qty;
            this.syncAddonDecreaseButton(control, qty);
          } else if (control.classList.contains('checkout-addon-product__add-btn')) {
            control.hidden = true;
          }
        });

        // Add in-cart border to product card
        const productCard = this.productsContainer.querySelector(`[data-product-id] [data-variant-id="${variantId}"]`)?.closest('[data-product-id]');
        if (productCard) {
          productCard.classList.add('checkout-addon-product--in-cart');
        }
      }
    });

    // Update checkout button text
    this.updateCheckoutButtonText();

    this.reorderInCartProductsToTop();
  }

  async loadProducts() {
    try {
      // Use dedicated collection - lighter payload, better caching than /collections/all
      const response = await fetch('/collections/addons-checkout-hide/products.json?limit=150&fields=id,title,body_html,product_type,tags,images,variants');
      const data = await response.json();

      // Process and set products
      const addonProducts = this.processProducts(data.products);
      this.setProducts(addonProducts);
    } catch (error) {
      console.error('Error loading checkout addons:', error);
      this.productsContainer.innerHTML = '<p>Unable to load products. Please try again.</p>';
    }
  }

  processProducts(products) {
    // Single-pass filter and transform - only keep necessary data
    const processed = products.reduce((acc, product) => {
      // Find first variant with WG_ or WGP_ SKU
      const validVariant = product.variants.find(v =>
        v.sku?.startsWith('WG_') || v.sku?.startsWith('WGP_')
      );
      if (!validVariant) {
        return acc;
      }

      // Only keep the data we actually render
      acc.push({
        id: product.id,
        title: product.title,
        body_html: product.body_html,
        product_type: product.product_type,
        tags: product.tags,
        images: product.images.slice(0, 1), // Only need first image
        variants: [{
          id: validVariant.id,
          sku: validVariant.sku,
          price: validVariant.price,
          compare_at_price: validVariant.compare_at_price
        }]
      });

      return acc;
    }, []);

    // Sort products: PORTAL_FEATURE first, then NEW, then the rest
    return this.sortProductsByPriority(processed);
  }

  /**
   * In-cart add-ons first so the member’s selection stays at the top (minimized row via CSS).
   */
  sortProductsWithInCartFirst(products) {
    if (!products || !products.length) return products;
    const qtyForVariant = (vid) => {
      if (vid == null) return 0;
      return (
        this.quantities[vid] ??
        this.quantities[String(vid)] ??
        0
      );
    };
    const inCart = [];
    const rest = [];
    products.forEach((p) => {
      const vid = p.variants?.[0]?.id;
      if (qtyForVariant(vid) > 0) inCart.push(p);
      else rest.push(p);
    });
    return [...inCart, ...rest];
  }

  /**
   * Move DOM nodes so cards with --in-cart appear first (matches sort after dynamic updates).
   */
  reorderInCartProductsToTop() {
    if (!this.productsContainer) return;
    const cards = Array.from(
      this.productsContainer.querySelectorAll('.checkout-addon-product')
    );
    if (cards.length < 2) return;
    const inCart = cards.filter((c) =>
      c.classList.contains('checkout-addon-product--in-cart')
    );
    const notInCart = cards.filter(
      (c) => !c.classList.contains('checkout-addon-product--in-cart')
    );
    if (!inCart.length) return;
    const frag = document.createDocumentFragment();
    inCart.forEach((el) => frag.appendChild(el));
    notInCart.forEach((el) => frag.appendChild(el));
    this.productsContainer.appendChild(frag);
  }

  sortProductsByPriority(products) {
    return products.sort((a, b) => {
      const aHasPortalFeature = a.tags?.includes('PORTAL_FEATURE');
      const bHasPortalFeature = b.tags?.includes('PORTAL_FEATURE');
      const aHasNew = a.tags?.includes('NEW');
      const bHasNew = b.tags?.includes('NEW');

      // PORTAL_FEATURE products come first
      if (aHasPortalFeature && !bHasPortalFeature) return -1;
      if (!aHasPortalFeature && bHasPortalFeature) return 1;

      // NEW products come second
      if (aHasNew && !bHasNew) return -1;
      if (!aHasNew && bHasNew) return 1;

      // Keep original order for the rest
      return 0;
    });
  }

  setProducts(products) {
    this.allProducts = products;

    // Hide loading spinner immediately
    if (this.loadingSpinner) {
      this.loadingSpinner.hidden = true;
    }

    // Initialize filter UI if container exists
    if (this.filterContainer && !this.filterContainer.hasAttribute('data-initialized')) {
      this.renderFilterUI();
      this.filterContainer.setAttribute('data-initialized', 'true');
    }

    // Check if we should auto-apply a diet preference filter
    const autoFilterValue = this.getAutoFilterPreference();
    
    if (autoFilterValue) {
      // Set the active filter state and switch to diet tab
      this.activeFilter = autoFilterValue;
      this.activeTab = 'diet';
      
      // Update UI to reflect the active filter
      this.updateFilterUI();
      
      // Filter and render the products
      this.filterProducts(autoFilterValue);
    } else {
      // Default to Deals filter
      this.activeFilter = 'Deals';
      this.activeTab = 'all';
      
      // Update UI to reflect the active filter
      this.updateFilterUI();
      
      // Filter and render the products with Deals filter
      this.filterProducts('Deals');
    }
  }

  onRenderComplete() {
    this.refreshQuantityDisplays();
    if (this._lastCart) {
      this.updateFooterSummary(this._lastCart);
    }
  }

  renderProductsProgressively(products) {
    if (!products || products.length === 0) {
      this.productsContainer.innerHTML = '<p>No add-on products available.</p>';
      return;
    }

    const sortedProducts = this.sortProductsWithInCartFirst(products);

    // Clear container
    this.productsContainer.innerHTML = '';

    const BATCH_SIZE = 8; // Smaller batches for smoother rendering
    let currentIndex = 0;

    const renderBatch = () => {
      const startTime = performance.now();
      const endIndex = Math.min(currentIndex + BATCH_SIZE, sortedProducts.length);
      const batch = sortedProducts.slice(currentIndex, endIndex);

      // Create a document fragment for better performance
      const fragment = document.createDocumentFragment();

      // Render and attach all at once per product
      batch.forEach(product => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = this.renderProduct(product);
        const productElement = wrapper.firstElementChild;
        this.attachEventListenersToProduct(productElement);
        fragment.appendChild(productElement);
      });

      this.productsContainer.appendChild(fragment);

      currentIndex = endIndex;

      // Schedule next batch if there are more products
      if (currentIndex < sortedProducts.length) {
        // Use timeout to give browser breathing room
        const elapsed = performance.now() - startTime;
        if (elapsed < 16) { // If batch took less than one frame
          requestAnimationFrame(renderBatch);
        } else {
          setTimeout(() => requestAnimationFrame(renderBatch), 0);
        }
      } else {
        // Rendering complete - update quantities from cart
        this.onRenderComplete();
      }
    };

    // Start rendering
    requestAnimationFrame(renderBatch);
  }

  // Label for the "Seasonal" chip: the season we're currently in (Northern
  // Hemisphere, solstice/equinox boundaries), with marketing overrides for
  // the holiday run (Ali, Nov 2026): THANKSGIVING from Nov 1, HOLIDAY from
  // Nov 27 through Dec 31. The filter value stays 'Seasonal' — that's the
  // product tag it queries, which doesn't change.
  currentSeasonLabel(date = new Date()) {
    const m = date.getMonth();
    const d = date.getDate();
    if (m === 10) return d < 27 ? 'THANKSGIVING' : 'HOLIDAY';
    if (m === 11) return 'HOLIDAY';
    if (m < 2 || (m === 2 && d < 20)) return 'WINTER';
    if (m < 5 || (m === 5 && d < 21)) return 'SPRING';
    if (m < 8 || (m === 8 && d < 22)) return 'SUMMER';
    return 'FALL';
  }

  renderFilterUI() {
    if (!this.filterContainer) return;

    const filterGroups = {
      all: [
        { label: 'DEALS', value: 'Deals' },
        { label: 'UNDER $10', value: 'Under10' },
        { label: 'BULK', value: 'Bulk' },
        { label: this.currentSeasonLabel(), value: 'Seasonal' },
        { label: 'KIDS', value: 'Kid-Friendly' }
      ],
      type: [
        { label: 'Bread', value: 'Bread' },
        { label: 'Pasta', value: 'Pasta' },
        { label: 'Pastry', value: 'Pastry' },
        { label: 'Pizza', value: 'Pizza' },
        { label: 'Spreads', value: 'Spreads' },
        { label: 'Other', value: 'Other' }
      ],
      diet: [
        { label: 'Gluten Free', value: 'Gluten Free' },
        { label: 'Vegan', value: 'Vegan' },
        { label: 'Protein', value: 'Protein' },
        { label: 'Dairy-Free', value: 'Dairy-Free' },
        { label: 'Low Sodium', value: 'Low Sodium' }
      ]
    };

    const html = `
      <div class="checkout-addons-filter">
        <!-- Tab Headers -->
        <div class="checkout-addons-filter__tabs">
          <button type="button" class="checkout-addons-filter__tab ${this.activeTab === 'all' ? 'active' : ''}" data-tab="all">ALL</button>
          <button type="button" class="checkout-addons-filter__tab ${this.activeTab === 'type' ? 'active' : ''}" data-tab="type">TYPE</button>
          <button type="button" class="checkout-addons-filter__tab ${this.activeTab === 'diet' ? 'active' : ''}" data-tab="diet">DIET</button>
          <button type="button" class="checkout-addons-filter__tab ${this.activeTab === 'search' ? 'active' : ''}" data-tab="search">SEARCH</button>
        </div>

        <!-- Tab Content -->
        <div class="checkout-addons-filter__content">
          <!-- ALL Tab -->
          <div class="checkout-addons-filter__panel ${this.activeTab === 'all' ? 'active' : ''}" data-panel="all">
            <div class="checkout-addons-filter__pills">
              ${filterGroups.all.map(filter => `
                <button type="button" class="checkout-addons-filter__pill ${this.activeFilter === filter.value ? 'active' : ''}" data-filter="${filter.value}">
                  ${filter.label}
                </button>
              `).join('')}
              <span class="checkout-addons-filter__pills-spacer" aria-hidden="true"></span>
            </div>
          </div>

          <!-- TYPE Tab -->
          <div class="checkout-addons-filter__panel ${this.activeTab === 'type' ? 'active' : ''}" data-panel="type">
            <div class="checkout-addons-filter__pills">
              ${filterGroups.type.map(filter => `
                <button type="button" class="checkout-addons-filter__pill ${this.activeFilter === filter.value ? 'active' : ''}" data-filter="${filter.value}">
                  ${filter.label}
                </button>
              `).join('')}
              <span class="checkout-addons-filter__pills-spacer" aria-hidden="true"></span>
            </div>
          </div>

          <!-- DIET Tab -->
          <div class="checkout-addons-filter__panel ${this.activeTab === 'diet' ? 'active' : ''}" data-panel="diet">
            <div class="checkout-addons-filter__pills">
              ${filterGroups.diet.map(filter => `
                <button type="button" class="checkout-addons-filter__pill ${this.activeFilter === filter.value ? 'active' : ''}" data-filter="${filter.value}">
                  ${filter.label}
                </button>
              `).join('')}
              <span class="checkout-addons-filter__pills-spacer" aria-hidden="true"></span>
            </div>
          </div>

          <!-- SEARCH Tab -->
          <div class="checkout-addons-filter__panel ${this.activeTab === 'search' ? 'active' : ''}" data-panel="search">
            <div class="checkout-addons-filter__search">
              <input type="text" placeholder="Search for products..." value="${this.searchTerm}" data-search-input>
              <svg class="checkout-addons-filter__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    `;

    this.filterContainer.innerHTML = html;
    this.attachFilterEventListeners();
  }

  attachFilterEventListeners() {
    if (!this.filterContainer) return;

    // Tab click handlers
    const tabs = this.filterContainer.querySelectorAll('[data-tab]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.tab;
        this.switchTab(tabName);
      });
    });

    // Filter pill click handlers
    const pills = this.filterContainer.querySelectorAll('[data-filter]');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const filterValue = pill.dataset.filter;
        this.applyFilter(filterValue);
      });
    });

    // Search input handler
    const searchInput = this.filterContainer.querySelector('[data-search-input]');
    if (searchInput) {
      let searchTelemetryTimer = null;
      searchInput.addEventListener('input', (e) => {
        this.searchTerm = e.target.value;
        this.applySearch(this.searchTerm);

        clearTimeout(searchTelemetryTimer);
        searchTelemetryTimer = setTimeout(() => {
          const term = (this.searchTerm || '').trim();
          if (term.length >= 2) {
            this.logStatsigEvent('funnel_addons_searched', term, { term_length: String(term.length) });
          }
        }, 700);
      });
    }
  }

  switchTab(tabName) {
    const previousTab = this.activeTab;
    this.activeTab = tabName;
    this.logStatsigEvent('funnel_addons_tab_switched', tabName, {
      tab: tabName,
      previous_tab: previousTab || ''
    });

    // Update tab buttons
    const tabs = this.filterContainer.querySelectorAll('[data-tab]');
    tabs.forEach(tab => {
      if (tab.dataset.tab === tabName) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    // Update panels
    const panels = this.filterContainer.querySelectorAll('[data-panel]');
    panels.forEach(panel => {
      if (panel.dataset.panel === tabName) {
        panel.classList.add('active');
      } else {
        panel.classList.remove('active');
      }
    });
  }

  getAutoFilterPreference() {
    try {
      // Use helper function if available, otherwise parse manually
      let dietValue = null;
      
      if (typeof window.getDietPreference === 'function') {
        dietValue = window.getDietPreference();
      } else {
        const preferenceData = localStorage.getItem('wildgrain_diet_preference');
        if (preferenceData) {
          try {
            const data = JSON.parse(preferenceData);
            const expiryDate = new Date(data.expiry);
            const now = new Date();
            
            // Only use if not expired
            if (now <= expiryDate) {
              dietValue = data.diet;
            }
          } catch (parseError) {
            // If parsing fails, treat as plain string (backwards compatibility)
            dietValue = preferenceData;
          }
        }
      }
      
      if (dietValue) {
        const diet = dietValue.toLowerCase();
        if (diet === 'gluten-free') {
          return 'Gluten Free';
        } else if (diet === 'high-protein') {
          return 'High Protein';
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting auto filter preference:', error);
      return null;
    }
  }

  updateFilterUI() {
    if (!this.filterContainer) return;

    // Update tab buttons
    const tabs = this.filterContainer.querySelectorAll('[data-tab]');
    tabs.forEach(tab => {
      if (tab.dataset.tab === this.activeTab) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    // Update panels
    const panels = this.filterContainer.querySelectorAll('[data-panel]');
    panels.forEach(panel => {
      if (panel.dataset.panel === this.activeTab) {
        panel.classList.add('active');
      } else {
        panel.classList.remove('active');
      }
    });

    // Update filter pills
    const pills = this.filterContainer.querySelectorAll('[data-filter]');
    pills.forEach(pill => {
      if (pill.dataset.filter === this.activeFilter) {
        pill.classList.add('active');
      } else {
        pill.classList.remove('active');
      }
    });
  }

  applyFilter(filterValue) {
    const requestedFilter = filterValue;
    // If clicking the same filter, deselect and show all
    if (this.activeFilter === filterValue) {
      this.activeFilter = 'all';
      filterValue = 'all';
    } else {
      this.activeFilter = filterValue;
    }

    this.logStatsigEvent('funnel_addons_filter_applied', this.activeFilter, {
      filter: this.activeFilter,
      clicked: requestedFilter,
      deselected: this.activeFilter === 'all' && requestedFilter !== 'all' ? 'true' : 'false'
    });

    // Update active state on pills
    const pills = this.filterContainer.querySelectorAll('[data-filter]');
    pills.forEach(pill => {
      if (pill.dataset.filter === filterValue) {
        pill.classList.add('active');
      } else {
        pill.classList.remove('active');
      }
    });

    // Clear search if switching to filter
    this.searchTerm = '';
    const searchInput = this.filterContainer.querySelector('[data-search-input]');
    if (searchInput) searchInput.value = '';

    // Apply the filter
    this.filterProducts(filterValue);
  }

  applySearch(searchTerm) {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      // If search is empty, show all products (already sorted by priority)
      this.renderProductsProgressively(this.allProducts);
      return;
    }

    const filteredProducts = this.allProducts.filter(product => {
      if (this.isProductInCart(product)) return true;

      const title = (product.title || '').toLowerCase();
      const description = (product.body_html || '').toLowerCase();
      const type = (product.product_type || '').toLowerCase();

      return title.includes(term) || description.includes(term) || type.includes(term);
    });

    // Apply priority sorting to search results (PORTAL_FEATURE first, then NEW, then rest)
    const sortedFilteredProducts = this.sortProductsByPriority(filteredProducts);
    this.renderProductsProgressively(sortedFilteredProducts);
  }

  attachEventListenersToProduct(productCard) {
    // Quantity controls
    const quantityControls = productCard.querySelectorAll('.checkout-addon-product__quantity');
    quantityControls.forEach(control => {
      const variantId = control.dataset.variantId;
      const minusBtn = control.querySelector('[data-action="minus"]');
      const plusBtn = control.querySelector('[data-action="plus"]');
      const input = control.querySelector('input');

      minusBtn.addEventListener('click', () => this.updateQuantity(variantId, -1, input));
      plusBtn.addEventListener('click', () => this.updateQuantity(variantId, 1, input));
      input.addEventListener('change', async (e) => {
        // Prevent multiple simultaneous requests
        if (this.updatingVariants.has(variantId)) return;

        const newValue = Math.max(0, parseInt(e.target.value) || 0);
        const previousQty = this.quantities[variantId] || 0;

        this.updatingVariants.add(variantId);
        this.setLoadingState(variantId, true);

        // Collect section IDs to render
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        // Update cart via API
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId.toString(),
              quantity: newValue,
              sections: sectionsToBundle
            })
          });

          if (response.ok) {
            const parsedState = await response.json();

            // Fetch full cart data and merge with sections
            const cartJson = await (await fetch('/cart.js')).json();
            cartJson['sections'] = parsedState['sections'];

            // Sync internal state
            this.syncWithCartData(cartJson);

            // Update UI
            this.refreshQuantityDisplays();

            // Notify other parts of the site that cart was updated
            try {
              if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
              }
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart: cartJson }
              }));
            } catch (pubsubError) {
              console.error('Error publishing cart update:', pubsubError);
            }

            // Show success animation if not removed
            if (newValue > 0) {
              this.showSuccessFeedback(variantId);
            }

            this.logAddonQuantityEvent(variantId, previousQty, newValue, 'input');
          } else {
            const errorData = await response.json();
            console.error('Error updating cart:', errorData);
            this.showErrorToast('Couldn\'t update quantity — please try again.');
            // Revert to current quantity
            this.updateQuantityDisplay(variantId);
          }
        } catch (error) {
          console.error('Error:', error);
          this.showErrorToast('Couldn\'t update quantity — please try again.');
          // Revert to current quantity
          this.updateQuantityDisplay(variantId);
        } finally {
          this.updatingVariants.delete(variantId);
          this.setLoadingState(variantId, false);
        }
      });
    });

    // Expand/collapse
    productCard.querySelectorAll('[data-action="expand"]').forEach(element => {
      element.addEventListener('click', (e) => {
        if (e.target.closest('[data-stop-propagation]')) return;
        this.expandProduct(productCard);
      });
    });

    productCard.querySelectorAll('[data-action="collapse"]').forEach(btn => {
      btn.addEventListener('click', () => this.collapseProduct(productCard));
    });

    // Stop propagation on quantity controls
    productCard.querySelectorAll('[data-stop-propagation]').forEach(element => {
      element.addEventListener('click', (e) => e.stopPropagation());
    });

    // Add to box buttons
    productCard.querySelectorAll('[data-action="add-to-box"]').forEach(btn => {
      btn.addEventListener('click', () => this.addToBox(btn.dataset.variantId));
    });
  }

  /** Returns true if a product's variant is currently in the cart. */
  isProductInCart(product) {
    const vid = product.variants?.[0]?.id;
    if (vid == null) return false;
    return (this.quantities[vid] || this.quantities[String(vid)] || 0) > 0;
  }

  filterProducts(filter) {
    if (filter === 'all') {
      // allProducts is already sorted by priority
      this.renderProductsProgressively(this.allProducts);
      return;
    }

    const filteredProducts = this.allProducts.filter(product => {
      // Always include products already in the cart so they stay visible
      if (this.isProductInCart(product)) return true;

      const typeStr = (product.product_type || product.productType || '').toString().toLowerCase();
      const variant = product.variants?.[0];

      // Product type filters
      if (filter === 'Bread') return typeStr.includes('bread');
      if (filter === 'Pasta') return typeStr.includes('pasta');
      if (filter === 'Pastry') return typeStr.includes('pastry');

      // Marketing filters
      if (filter === 'Deals') {
        // Show products with compare_at_price (on sale)
        return variant?.compare_at_price && variant.compare_at_price > variant.price;
      }
      if (filter === 'Under10') {
        const raw = variant?.price;
        const dollars = typeof raw === 'string' ? parseFloat(raw) : raw / 100;
        return !isNaN(dollars) && dollars < 10;
      }
      if (filter === 'Bulk') {
        // Show products with SKU starting with WGP
        return variant?.sku?.startsWith('WGP');
      }
      if (filter === 'Seasonal') return product.tags?.includes('SEASONAL');
      if (filter === 'Kid-Friendly') return product.tags?.includes('KID_FRIENDLY');

      // Dietary & Special tags
      if (filter === 'Gluten Free') return product.tags?.includes('GLUTEN_FREE');
      if (filter === 'Vegan') return product.tags?.includes('VEGAN') || product.tags?.includes('Vegan');
      if (filter === 'Protein') return product.tags?.includes('HIGH_PROTEIN');
      if (filter === 'Dairy-Free') return product.tags?.includes('DAIRY_FREE');
      if (filter === 'Low Sodium') return product.tags?.includes('LOW_SODIUM');

      // New filters
      if (filter === 'Spreads') return product.tags?.includes('SPREAD');
      if (filter === 'Pizza') {
        const titleStr = (product.title || '').toLowerCase();
        return titleStr.includes('pizza') || typeStr.includes('pizza');
      }

      // Other filter: include PANTRY tag or product types not Bread/Pasta/Pastry
      if (filter === 'Other') {
        if (product.tags?.includes('PANTRY')) return true;
        if ((product.title || '').toLowerCase().includes('pizza')) return true;
        return !(typeStr.includes('bread') || typeStr.includes('pasta') || typeStr.includes('pastry'));
      }

      return false;
    });

    // Apply priority sorting to filtered results (PORTAL_FEATURE first, then NEW, then rest)
    const sortedFilteredProducts = this.sortProductsByPriority(filteredProducts);
    this.renderProductsProgressively(sortedFilteredProducts);
  }


  async addToBox(variantId) {
    // Prevent multiple simultaneous requests
    if (this.updatingVariants.has(variantId)) return;

    this.updatingVariants.add(variantId);
    this.setLoadingState(variantId, true);

    // Collect section IDs to render
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    // Dispatch event to signal we're adding from addons drawer
    document.dispatchEvent(new CustomEvent('checkout-addons:adding-to-cart'));

    // Add to cart immediately
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }],
          sections: sectionsToBundle
        })
      });

      if (response.ok) {
        const parsedState = await response.json();

        // Fetch full cart data and merge with sections
        const cartJson = await (await fetch('/cart.js')).json();
        cartJson['sections'] = parsedState['sections'];

        // Sync internal state
        this.syncWithCartData(cartJson);

        // Update UI
        this.refreshQuantityDisplays();

        // Notify other parts of the site that cart was updated
        try {
          if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          }
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cartJson }
          }));
        } catch (pubsubError) {
          console.error('Error publishing cart update:', pubsubError);
        }

        // Add success animation
        this.showSuccessFeedback(variantId);

        const product = this.getProductByVariantId(variantId);
        const variant = product?.variants?.find(v => String(v.id) === String(variantId));
        this.logStatsigEvent('funnel_addons_item_added', product?.title || product?.handle || String(variantId), {
          variant_id: String(variantId),
          product_id: String(product?.id || ''),
          handle: product?.handle || '',
          price: String(variant?.price ? (Number(variant.price) / 100) : ''),
          active_tab: this.activeTab || '',
          active_filter: this.activeFilter || ''
        });
      } else {
        console.error('Error adding item to cart');
        this.showErrorToast('Couldn\'t add item — please try again.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorToast('Couldn\'t add item — please try again.');
    } finally {
      this.updatingVariants.delete(variantId);
      this.setLoadingState(variantId, false);
    }
  }

  expandProduct(productCard) {
    // Collapse any currently expanded products
    const expandedProducts = this.productsContainer.querySelectorAll('.checkout-addon-product--expanded');
    expandedProducts.forEach(expanded => {
      if (expanded !== productCard) {
        this.collapseProduct(expanded);
      }
    });

    // Expand the clicked product
    productCard.classList.add('checkout-addon-product--expanded');
    productCard.querySelector('.checkout-addon-product__collapsed').hidden = true;
    const expandedSection = productCard.querySelector('.checkout-addon-product__expanded');
    expandedSection.hidden = false;

    const productId = productCard.dataset.productId;
    const product = this.allProducts.find(p => p.id == productId);

    this.logStatsigEvent('funnel_addons_product_expanded', product?.title || product?.handle || String(productId), {
      product_id: String(productId || ''),
      handle: product?.handle || ''
    });

    // Lazy-load description content on first expand
    const descriptionPlaceholder = expandedSection.querySelector('[data-description-placeholder]');
    if (descriptionPlaceholder && expandedSection.hasAttribute('data-lazy-content')) {
      if (product) {
        descriptionPlaceholder.innerHTML = product.body_html || '<p>No description available.</p>';
        expandedSection.removeAttribute('data-lazy-content');
      }
    }

    if (product) {
      this.loadBundleContentsForAddon(expandedSection, product);
    }

    // Keep filter tabs visible: only scroll if needed
    requestAnimationFrame(() => {
      productCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  collapseProduct(productCard) {
    productCard.classList.remove('checkout-addon-product--expanded');
    productCard.querySelector('.checkout-addon-product__collapsed').hidden = false;
    productCard.querySelector('.checkout-addon-product__expanded').hidden = true;
  }

  getImageUrl(imageSrc, size = '300x300') {
    if (!imageSrc) return '';
    // Shopify image URL format: add size before file extension
    const url = typeof imageSrc === 'string' ? imageSrc : (imageSrc?.src || '');
    if (!url) return '';

    // Insert size parameter before the file extension
    return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
  }

  renderProduct(product) {
    const variant = product.variants[0];
    const price = this.formatMoney(variant.price);
    const comparePrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;
    const isDeal = comparePrice !== null && variant.compare_at_price > variant.price;
    const imageData = product.images[0];
    const imageSrc = this.getImageUrl(imageData, '300x300');

    const savingsAmount = isDeal ? variant.compare_at_price - variant.price : 0;
    const savingsTag = savingsAmount > 0
      ? `<span class="checkout-addon-product__savings">Save ${this.formatMoney(String(savingsAmount))}</span>`
      : '';

    const priceBlock = `
      <span class="checkout-addon-product__price-current">${price}</span>
      ${comparePrice ? `<span class="checkout-addon-product__price-compare">${comparePrice}</span>` : ''}
    `;

    return `
      <div class="checkout-addon-product" data-product-id="${product.id}">
        <div class="checkout-addon-product__collapsed" data-action="expand">
          <div class="checkout-addon-product__image">
            ${imageSrc ? `<img src="${imageSrc}" alt="${product.title}" loading="lazy" width="300" height="300">` : WG_ADDON_IMAGE_PLACEHOLDER}
            ${isDeal ? '<span class="checkout-addon-product__badge checkout-addon-product__badge--deal">DEAL</span>' : ''}
          </div>
          <div class="checkout-addon-product__info">
            ${savingsTag}
            <h3 class="checkout-addon-product__title">${product.title}</h3>
            <div class="checkout-addon-product__price checkout-addon-product__price--duplicate">
              ${priceBlock}
            </div>
          </div>
          <div class="checkout-addon-product__actions" data-stop-propagation>
            <button type="button" class="checkout-addon-product__add-btn" data-variant-id="${variant.id}" data-action="add-to-box" aria-label="Add to box, ${price}${comparePrice ? `, was ${comparePrice}` : ''}">
              <span class="checkout-addon-product__add-btn-text">+ ADD</span>
              <span class="checkout-addon-product__add-btn-prices">${priceBlock}</span>
            </button>
            <div class="checkout-addon-product__quantity" data-variant-id="${variant.id}" hidden>
              <button type="button" data-action="minus" class="checkout-addon-product__qty-decrease" aria-label="Decrease quantity">${WG_ADDON_MINUS_SVG}</button>
              <input type="text" name="quantity-${variant.id}" value="0" inputmode="numeric" pattern="[0-9]*" aria-label="Quantity">
              <button type="button" data-action="plus" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
        <div class="checkout-addon-product__expanded" hidden data-lazy-content="true">
          <div class="checkout-addon-product__expanded-header">
            <button type="button" class="checkout-addon-product__collapse" data-action="collapse" aria-label="Collapse">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <h3 class="checkout-addon-product__title">${product.title}</h3>
          </div>
          <div class="checkout-addon-product__expanded-content">
            <div class="checkout-addon-product__image">
              ${imageSrc ? `<img src="${imageSrc}" alt="${product.title}" loading="lazy" width="300" height="300">` : WG_ADDON_IMAGE_PLACEHOLDER}
            </div>
            <div class="checkout-addon-product__description" data-description-placeholder>
              <!-- Description loaded on expand -->
            </div>
          </div>
          <div class="checkout-addon-product__expanded-footer">
            <div class="checkout-addon-product__price checkout-addon-product__price--duplicate">
              ${priceBlock}
            </div>
            <button type="button" class="checkout-addon-product__add-btn" data-variant-id="${variant.id}" data-action="add-to-box" aria-label="Add to box, ${price}${comparePrice ? `, was ${comparePrice}` : ''}">
              <span class="checkout-addon-product__add-btn-text">+ ADD</span>
              <span class="checkout-addon-product__add-btn-prices">${priceBlock}</span>
            </button>
            <div class="checkout-addon-product__quantity" data-variant-id="${variant.id}" hidden>
              <button type="button" data-action="minus" class="checkout-addon-product__qty-decrease" aria-label="Decrease quantity">${WG_ADDON_MINUS_SVG}</button>
              <input type="text" name="quantity-${variant.id}" value="0" inputmode="numeric" pattern="[0-9]*" aria-label="Quantity">
              <button type="button" data-action="plus" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async updateQuantity(variantId, change, input) {
    // Prevent multiple simultaneous requests
    if (this.updatingVariants.has(variantId)) return;

    const currentQty = this.quantities[variantId] || 0;
    const newQty = Math.max(0, currentQty + change);

    this.updatingVariants.add(variantId);
    this.setLoadingState(variantId, true);

    // Collect section IDs to render
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    // Update cart via API
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId.toString(),
          quantity: newQty,
          sections: sectionsToBundle
        })
      });

      if (response.ok) {
        const parsedState = await response.json();

        // Fetch full cart data and merge with sections
        const cartJson = await (await fetch('/cart.js')).json();
        cartJson['sections'] = parsedState['sections'];

        // Sync internal state
        this.syncWithCartData(cartJson);

        // Update UI
        this.refreshQuantityDisplays();

        // Notify other parts of the site that cart was updated
        try {
          if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          }
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cartJson }
          }));
        } catch (pubsubError) {
          console.error('Error publishing cart update:', pubsubError);
        }

        // Show success animation if not removed
        if (newQty > 0) {
          this.showSuccessFeedback(variantId);
        }

        this.logAddonQuantityEvent(variantId, currentQty, newQty, change > 0 ? 'plus_button' : 'minus_button');
      } else {
        const errorData = await response.json();
        console.error('Error updating cart:', errorData);
        this.showErrorToast('Couldn\'t update quantity — please try again.');
        // Revert to current quantity
        this.updateQuantityDisplay(variantId);
      }
    } catch (error) {
      console.error('Error:', error);
      this.showErrorToast('Couldn\'t update quantity — please try again.');
      // Revert to current quantity
      this.updateQuantityDisplay(variantId);
    } finally {
      this.updatingVariants.delete(variantId);
      this.setLoadingState(variantId, false);
    }
  }

  updateQuantityDisplay(variantId) {
    const qty = this.quantities[variantId] || 0;
    const quantityControls = this.productsContainer.querySelectorAll(`[data-variant-id="${variantId}"]`);
    quantityControls.forEach(control => {
      if (control.classList.contains('checkout-addon-product__quantity')) {
        const input = control.querySelector('input');
        if (input) input.value = qty;
        this.syncAddonDecreaseButton(control, qty);
      }
    });
  }

  /** Minus control shows trash when qty is 1 (same as bundle box builder). */
  syncAddonDecreaseButton(quantityControl, qty) {
    const btn = quantityControl.querySelector('button[data-action="minus"]');
    if (!btn) return;
    if (qty === 1) {
      btn.innerHTML = WG_ADDON_TRASH_SVG;
      btn.setAttribute('aria-label', 'Remove item from cart');
      btn.classList.add('checkout-addon-product__qty-decrease--trash');
    } else {
      btn.innerHTML = WG_ADDON_MINUS_SVG;
      btn.setAttribute('aria-label', 'Decrease quantity');
      btn.classList.remove('checkout-addon-product__qty-decrease--trash');
    }
  }

  showAddButton(variantId) {
    const controls = this.productsContainer.querySelectorAll(`[data-variant-id="${variantId}"]`);
    controls.forEach(control => {
      if (control.classList.contains('checkout-addon-product__quantity')) {
        control.hidden = true;
      } else if (control.classList.contains('checkout-addon-product__add-btn')) {
        control.hidden = false;
      }
    });
  }

  setLoadingState(variantId, isLoading) {
    const controls = this.productsContainer.querySelectorAll(`[data-variant-id="${variantId}"]`);
    controls.forEach(control => {
      if (isLoading) {
        control.style.opacity = '0.6';
        control.style.pointerEvents = 'none';
        if (control.classList.contains('checkout-addon-product__add-btn')) {
          control.classList.add('checkout-addon-product__add-btn--loading');
          control.setAttribute('aria-busy', 'true');
        }
      } else {
        control.style.opacity = '1';
        control.style.pointerEvents = 'auto';
        if (control.classList.contains('checkout-addon-product__add-btn')) {
          control.classList.remove('checkout-addon-product__add-btn--loading');
          control.removeAttribute('aria-busy');
        }
      }
    });

    // Find product cards for this variant
    const productCards = this.productsContainer.querySelectorAll(`[data-product-id]`);
    productCards.forEach(card => {
      const cardVariantId = card.querySelector(`[data-variant-id="${variantId}"]`)?.dataset.variantId;
      if (cardVariantId === variantId) {
        if (isLoading) {
          card.classList.add('checkout-addon-product--loading');
        } else {
          card.classList.remove('checkout-addon-product--loading');
        }
      }
    });
  }

  setCheckoutButtonLoading(isLoading) {
    const btn = this.checkoutButton;
    if (!btn) return;
    if (isLoading) {
      btn.disabled = true;
      btn.classList.add('checkout-addons-checkout-btn--loading');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.disabled = false;
      btn.classList.remove('checkout-addons-checkout-btn--loading');
      btn.removeAttribute('aria-busy');
    }
  }

  showSuccessFeedback(variantId) {
    const productCards = this.productsContainer.querySelectorAll(`[data-product-id]`);
    let productTitle = '';
    productCards.forEach(card => {
      const cardVariantId = card.querySelector(`[data-variant-id="${variantId}"]`)?.dataset.variantId;
      if (cardVariantId === variantId) {
        if (card.classList.contains('checkout-addon-product--expanded')) {
          this.collapseProduct(card);
        }

        card.classList.add('checkout-addon-product--success');
        card.classList.add('checkout-addon-product--in-cart');
        setTimeout(() => {
          card.classList.remove('checkout-addon-product--success');
        }, 600);

        productTitle = card.querySelector('.checkout-addon-product__title')?.textContent?.trim() || '';
      }
    });

    this.showAddedToast(productTitle);
  }

  showAddedToast(productTitle) {
    let toast = document.querySelector('.checkout-addons-toast');
    if (toast) {
      clearTimeout(toast._hideTimer);
      clearTimeout(toast._removeTimer);
      toast.classList.remove('checkout-addons-toast--show', 'checkout-addons-toast--hide');
    } else {
      toast = document.createElement('div');
      toast.className = 'checkout-addons-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    const safeName = productTitle ? this.escapeHtml(productTitle) : '';
    toast.innerHTML = `
      <div class="checkout-addons-toast__icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
      </div>
      <div class="checkout-addons-toast__text">
        <span class="checkout-addons-toast__heading">Added to your cart</span>
        ${safeName ? `<span class="checkout-addons-toast__product">${safeName}</span>` : ''}
      </div>
    `;

    void toast.offsetWidth;
    requestAnimationFrame(() => toast.classList.add('checkout-addons-toast--show'));

    toast._hideTimer = setTimeout(() => {
      toast.classList.add('checkout-addons-toast--hide');
      toast.classList.remove('checkout-addons-toast--show');
      toast._removeTimer = setTimeout(() => toast.remove(), 400);
    }, 2800);
  }

  showErrorToast(message) {
    let toast = document.querySelector('.checkout-addons-toast');
    if (toast) {
      clearTimeout(toast._hideTimer);
      clearTimeout(toast._removeTimer);
      toast.classList.remove('checkout-addons-toast--show', 'checkout-addons-toast--hide');
    } else {
      toast = document.createElement('div');
      toast.className = 'checkout-addons-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toast);
    }

    toast.innerHTML = `
      <div class="checkout-addons-toast__icon checkout-addons-toast__icon--error">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </div>
      <div class="checkout-addons-toast__text">
        <span class="checkout-addons-toast__heading">${this.escapeHtml(message)}</span>
      </div>
    `;

    void toast.offsetWidth;
    requestAnimationFrame(() => toast.classList.add('checkout-addons-toast--show'));

    toast._hideTimer = setTimeout(() => {
      toast.classList.add('checkout-addons-toast--hide');
      toast.classList.remove('checkout-addons-toast--show');
      toast._removeTimer = setTimeout(() => toast.remove(), 400);
    }, 3500);
  }

  updateCheckoutButtonText() {
    if (!this.checkoutButton) return;

    const addonCount = Object.keys(this.quantities).reduce((total, variantId) => {
      const qty = this.quantities[variantId];
      const sku = (this.cartItemsSKUs?.[variantId] || '').toUpperCase();
      const productType = (this.cartItemsProductTypes?.[variantId] || '').toLowerCase();
      const isBoxItem = sku.startsWith('WGB') || sku.includes('_BOX') || productType === 'bundle';
      if (qty > 0 && !isBoxItem) {
        return total + qty;
      }
      return total;
    }, 0);

    const text =
      addonCount === 0
        ? 'CONTINUE TO CHECKOUT'
        : addonCount === 1
          ? 'CHECKOUT WITH 1 ADDON'
          : `CHECKOUT WITH ${addonCount} ADDONS`;

    if (this.checkoutButtonLabel) {
      this.checkoutButtonLabel.textContent = text;
    } else {
      this.checkoutButton.textContent = text;
    }
  }

  async proceedToCheckout() {
    if (this.checkoutButton?.classList.contains('checkout-addons-checkout-btn--loading')) {
      return;
    }

    const cart = this._lastCart;
    const checkoutMeta = {
      cart_item_count: String((cart && cart.item_count) || 0),
      cart_total: String(cart && cart.total_price ? (cart.total_price / 100) : 0),
      drawer_skipped: this.getCookie('noAddonModal') ? 'true' : 'false',
      source: 'member_deals_drawer'
    };
    // Fire canonical funnel_checkout_started too — box-builder flows bypass the
    // checkout-button click selector in statsig-acq-funnel.liquid.
    this.logStatsigEvent('funnel_addons_checkout_started', 'member_deals', checkoutMeta);
    this.logStatsigEvent('funnel_checkout_started', window.location.pathname, checkoutMeta);
    this.flushStatsigEvents();

    this.setCheckoutButtonLoading(true);

    try {
      // Items are already in cart from individual add/update actions

      let cart = null;

      // Check for duplicate box items by fetching the actual Shopify cart
      try {
        const cartResponse = await fetch('/cart.js');
        cart = await cartResponse.json();

        if (cart && cart.items && Array.isArray(cart.items)) {
          const boxItems = cart.items.filter(item => item.sku && item.sku.startsWith('WGB_'));

          if (boxItems.length > 1) {
            const updates = {};
            const itemsToRemove = boxItems.slice(0, -1);

            for (const item of itemsToRemove) {
              updates[item.key] = 0;
            }

            await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ updates }),
            });
          }
        }
      } catch (error) {
        console.error('Error cleaning duplicate cart items:', error);
      }

      const has4ItemBox =
        cart &&
        cart.items &&
        cart.items.some(item => {
          const variantTitle = (item.variant_title || '').toLowerCase();
          return variantTitle.includes('4 items');
        });

      const autoDiscount = this.getCookie('wg_auto_discount');
      const discountCode = this.getCookie('wg_discount_code');

      let url = '/checkout';
      if (!has4ItemBox) {
        if (autoDiscount) {
          url += `?discount=${autoDiscount}`;
        } else if (discountCode) {
          url += `?discount=${discountCode}`;
        }
      }
      // Pre-fill the checkout's email for visitors we already know (klaviyo-onsite.liquid).
      if (window.wgCheckoutUrl) url = window.wgCheckoutUrl(url);
      window.location.href = url;
    } catch (e) {
      console.error('Checkout preparation failed:', e);
      this.setCheckoutButtonLoading(false);
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  async refreshCartDrawer() {
    // Find the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const sectionId = cartDrawer.getAttribute('data-section-id');
    if (!sectionId) return;

    const cartId = `CartDrawer-${sectionId}`;
    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      // Fetch the updated cart drawer HTML
      const rootUrl = window.FoxTheme?.routes?.root_url || '/';
      const response = await fetch(`${rootUrl}?section_id=${sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId);
      if (newCartContent) {
        cartElement.innerHTML = newCartContent.innerHTML;
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  getStorefrontToken() {
    const el = document.querySelector('products-bundle[data-storefront-token]') ||
      document.querySelector('bundle-contents[data-storefront-token]');
    return el?.dataset.storefrontToken || '94fa5e84d61642557dfe538209b86a4d';
  }

  getShopDomain() {
    const el = document.querySelector('products-bundle[data-shop-domain]') ||
      document.querySelector('bundle-contents[data-shop-domain]');
    return el?.dataset.shopDomain || window.Shopify?.shop || location.hostname;
  }

  getAssetBaseUrl() {
    const link = document.querySelector('link[href*="/assets/"]');
    if (link) {
      const href = link.getAttribute('href');
      const idx = href.indexOf('/assets/');
      if (idx !== -1) return href.substring(0, idx + '/assets/'.length);
    }
    return null;
  }

  ensureBundleCssLoaded() {
    if (document.querySelector('link[href*="bundle-contents.css"]')) return;
    const base = this.getAssetBaseUrl();
    if (!base) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${base}bundle-contents.css`;
    document.head.appendChild(link);
  }

  async fetchBundleFromWildgrainApi(sku) {
    try {
      const res = await fetch(
        `https://api.wildgrain.com/api/bundle/storefront/${encodeURIComponent(sku)}`
      );
      if (!res.ok) return null;
      const json = await res.json();
      if (!json.success || !json.items?.length) return null;
      return json.items.map((item) => ({
        quantity: item.quantity,
        variantShopifyId: item.variant_shopify_id,
        productName: item.product_name || item.sku,
        handle: item.handle || '',
      }));
    } catch {
      return null;
    }
  }

  async enrichWithStorefrontImages(items, token, storefrontUrl) {
    const ids = items.map((i) => i.variantShopifyId).filter(Boolean);
    if (!ids.length) {
      return items.map((i) => ({
        quantity: i.quantity,
        productVariant: {
          title: i.productName,
          image: null,
          product: { title: i.productName, handle: i.handle },
        },
      }));
    }
    try {
      const fragments = ids.map(
        (id, idx) =>
          `v${idx}: node(id: "gid://shopify/ProductVariant/${id}") {
            ... on ProductVariant { id title image { url altText } product { title handle } }
          }`
      );
      const res = await fetch(storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: `{ ${fragments.join('\n')} }` }),
      });
      const json = await res.json();
      const data = json?.data || {};
      const map = {};
      Object.values(data).forEach((n) => {
        if (n?.id) map[n.id.replace('gid://shopify/ProductVariant/', '')] = n;
      });
      return items.map((i) => {
        const vd = map[i.variantShopifyId] || {};
        return {
          quantity: i.quantity,
          productVariant: {
            title: vd.title || i.productName,
            image: vd.image || null,
            product: {
              title: vd.product?.title || i.productName,
              handle: vd.product?.handle || i.handle,
            },
          },
        };
      });
    } catch {
      return items.map((i) => ({
        quantity: i.quantity,
        productVariant: {
          title: i.productName,
          image: null,
          product: { title: i.productName, handle: i.handle },
        },
      }));
    }
  }

  async fetchBundleFromStorefront(variantId, token, storefrontUrl) {
    const gid = `gid://shopify/ProductVariant/${variantId}`;
    const query = `query BundleComponents($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          id
          requiresComponents
          components(first: 20) {
            nodes {
              quantity
              productVariant {
                id title image { url altText }
                product { title handle }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    }`;
    try {
      const res = await fetch(storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables: { id: gid } }),
      });
      const json = await res.json();
      const node = json?.data?.node;
      if (!node?.requiresComponents) return null;
      return node.components?.nodes ?? [];
    } catch {
      return null;
    }
  }

  async loadBundleContentsForAddon(expandedSection, product) {
    const variant = product.variants?.[0];
    if (!variant) return;

    const sku = (variant.sku || '').toUpperCase();
    if (!sku.includes('WGP')) return;

    const cacheKey = variant.sku || String(variant.id);
    const contentArea = expandedSection.querySelector('.checkout-addon-product__expanded-content');
    if (!contentArea) return;

    if (contentArea.querySelector('.bundle-contents')) return;

    if (this._bundleCache[cacheKey]) {
      this.renderBundleContentsInAddon(contentArea, this._bundleCache[cacheKey]);
      return;
    }

    const token = this.getStorefrontToken();
    const shopDomain = this.getShopDomain();
    const storefrontUrl = `https://${shopDomain}/api/2025-01/graphql.json`;

    try {
      let components = await this.fetchBundleFromWildgrainApi(variant.sku);

      if (components?.length) {
        components = await this.enrichWithStorefrontImages(components, token, storefrontUrl);
      } else {
        components = await this.fetchBundleFromStorefront(variant.id, token, storefrontUrl);
      }

      if (components?.length) {
        this._bundleCache[cacheKey] = components;
        this.renderBundleContentsInAddon(contentArea, components);
      }
    } catch (e) {
      /* silent */
    }
  }

  renderBundleContentsInAddon(container, components) {
    if (!components.length) return;

    this.ensureBundleCssLoaded();

    const listHtml = components
      .map((c) => {
        const pv = c.productVariant;
        const title = pv.product?.title ?? pv.title;
        const imgUrl = pv.image?.url;
        const alt = (pv.image?.altText || title || '').replace(/"/g, '&quot;');

        return `<li class="bundle-contents__item bundle-contents__item--static">
          ${imgUrl
            ? `<div class="bundle-contents__img">
                <img src="${imgUrl}&width=120" alt="${alt}" width="60" height="60" loading="lazy">
              </div>`
            : ''
          }
          <div class="bundle-contents__details">
            <span class="bundle-contents__qty">${c.quantity} &times;</span>
            <span class="bundle-contents__title">${title}</span>
          </div>
        </li>`;
      })
      .join('');

    const section = document.createElement('div');
    section.className = 'bundle-contents';
    section.innerHTML = `
      <div class="bundle-contents__header">
        <h3 class="bundle-contents__heading" style="font-size:1.6rem;margin:0;">What's in the Box</h3>
      </div>
      <ul class="bundle-contents__list" role="list">${listHtml}</ul>
    `;

    const description = container.querySelector('.checkout-addon-product__description');
    if (description) {
      description.insertAdjacentElement('afterend', section);
    } else {
      container.appendChild(section);
    }
  }

  formatMoney(price) {
    // Shopify products.json returns prices in cents, need to divide by 100
    const amount = typeof price === 'string' ? parseFloat(price) : price / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    // Remove .00 for whole dollar amounts
    return formatted.replace(/\.00$/, '');
  }
}

customElements.define('checkout-addons-drawer', CheckoutAddonsDrawer);

// Close drawer when clicking overlay or close button
document.addEventListener('click', (e) => {
  if (e.target.matches('[aria-controls="CheckoutAddonsDrawer"]') ||
      e.target.closest('[aria-controls="CheckoutAddonsDrawer"]')) {
    const drawer = document.getElementById('CheckoutAddonsDrawer');
    if (drawer) {
      drawer.close();
    }
  }
});

// Inject CSS styles for filter UI
if (!document.getElementById('checkout-addons-filter-styles')) {
  const style = document.createElement('style');
  style.id = 'checkout-addons-filter-styles';
  style.textContent = `
    .checkout-addons-filter {
      width: 100%;
      margin-bottom: 0;
    }

    .checkout-addons-filter__tabs {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      align-items: stretch;
      border-bottom: 1px solid #DDDDDD;
      margin-bottom: 0;
      gap: 0;
      width: 100%;
      box-sizing: border-box;
    }

    .checkout-addons-filter__tab {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-width: 0;
      margin: 0;
      padding: 0.35rem 0.5rem 0.4rem;
      line-height: 1.15;
      font-size: 14px;
      font-weight: 500;
      color: #717171;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      border-radius: 0;
      cursor: pointer;
      transition: color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-family: inherit;
      text-align: center;
      box-sizing: border-box;
      -webkit-appearance: none;
      appearance: none;
    }

    .checkout-addons-filter__tab:not(.active) {
      opacity: 0.85;
    }

    .checkout-addons-filter__tab:hover {
      color: #222222;
      border-bottom-color: #DDDDDD;
      opacity: 1;
    }

    .checkout-addons-filter__tab.active {
      color: #222222;
      border-bottom-color: #222222;
      opacity: 1;
    }

    .checkout-addons-filter__content {
      position: relative;
      min-height: 0;
    }

    .checkout-addons-filter__panel {
      display: none;
      animation: fadeIn 0.3s ease;
    }

    .checkout-addons-filter__panel.active {
      display: block;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .checkout-addons-filter__pills {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: center;
      align-items: center;
      padding: 0.625rem 0 0.25rem;
    }

    .checkout-addons-filter__pill {
      flex-shrink: 0;
      margin: 0 !important;
      text-transform: uppercase !important;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.625rem 1.35rem !important;
      font-size: 1.0625rem !important;
      font-weight: 600;
      color: #333;
      background: #fff;
      border: 1.5px solid #d4d4d4;
      border-radius: 999px;
      cursor: pointer;
      transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
      white-space: nowrap;
      letter-spacing: 0.03em;
      line-height: 1.2 !important;
      height: auto !important;
      min-height: 0 !important;
      vertical-align: middle;
      -webkit-appearance: none;
      appearance: none;
    }

    .checkout-addons-filter__pill:hover {
      border-color: #999;
      background: #fafafa;
    }

    .checkout-addons-filter__pill.active {
      background: #ea580c;
      border-color: #ea580c;
      color: #fff;
      box-shadow: 0 2px 8px rgba(234, 88, 12, 0.2);
    }

    .checkout-addons-filter__search {
      position: relative;
      max-width: 500px;
      margin: 0 auto;
      margin-top: 10px;
    }

    .checkout-addons-filter__search input {
      width: 100%;
      padding: 0.875rem 1rem 0.875rem 3rem;
      font-size: 1rem;
      border: 1px solid #DDDDDD;
      border-radius: 2rem;
      outline: none;
      transition: all 0.15s ease;
      font-family: inherit;
    }

    .checkout-addons-filter__search input:focus {
      border-color: #222222;
      box-shadow: 0 0 0 1px #222222;
    }

    .checkout-addons-filter__search-icon {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: #717171;
      pointer-events: none;
    }

    /* Responsive: scrollable row with even gaps (no negative margins — avoids underline drift) */
    @media (max-width: 749px) {
      .checkout-addons-filter__tabs {
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: stretch;
        gap: 0.85rem;
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x;
        overscroll-behavior-x: contain;
        scrollbar-width: none;
        padding-bottom: 0;
        margin-left: 0;
        margin-right: 0;
        padding-left: 0;
        padding-right: 0;
      }
      .checkout-addons-filter__tabs::-webkit-scrollbar { display: none; }

      .checkout-addons-filter__tab {
        flex: 0 0 auto;
        width: auto;
        min-width: 4.25rem;
        padding: 0.5rem 0.85rem 0.6rem;
        line-height: 1.2;
        font-size: 15px;
        letter-spacing: 0.04em;
      }

      .checkout-addons-filter__pills {
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: center;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x;
        overscroll-behavior-x: contain;
        scrollbar-width: none;
        gap: 0.7rem;
        padding: 1.1rem 0 0.85rem;
        margin-left: -1.1rem;
        margin-right: -1.1rem;
        width: calc(100% + 2.2rem);
      }
      .checkout-addons-filter__pills > :first-child {
        margin-left: 1.1rem;
      }
      .checkout-addons-filter__pills-spacer {
        flex-shrink: 0;
        width: 1.1rem;
        min-width: 1.1rem;
        display: block;
        height: 1px;
        pointer-events: none;
      }
      .checkout-addons-filter__pills::-webkit-scrollbar { display: none; }

      .checkout-addons-filter__pill {
        padding: 0.7rem 1.45rem !important;
        font-size: 1.5rem !important;
      }

      .checkout-addons-filter__search input {
        font-size: 1.125rem;
        padding: 1.1rem 1.1rem 1.1rem 3.15rem;
      }
    }

    .checkout-addons-toast {
      position: fixed;
      top: 1.25rem;
      left: 50%;
      transform: translateX(-50%) translateY(-120%);
      z-index: 10030;
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1.15rem 1.5rem;
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.08);
      opacity: 0;
      transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: none;
      width: max-content;
      max-width: calc(100vw - 2rem);
    }

    .checkout-addons-toast--show {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }

    .checkout-addons-toast--hide {
      opacity: 0;
      transform: translateX(-50%) translateY(-60%);
      transition: opacity 0.25s ease-in, transform 0.25s ease-in;
    }

    .checkout-addons-toast__icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #22c55e;
      color: #fff;
      animation: checkout-toast-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
    }

    .checkout-addons-toast__icon--error {
      background: #ef4444;
    }

    @keyframes checkout-toast-pop {
      0% { transform: scale(0); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }

    .checkout-addons-toast__text {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      min-width: 0;
    }

    .checkout-addons-toast__heading {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1a1a1a;
      letter-spacing: -0.01em;
      line-height: 1.3;
    }

    .checkout-addons-toast__product {
      font-size: 1.0625rem;
      color: #555;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 749px) {
      .checkout-addons-toast {
        top: max(1rem, env(safe-area-inset-top, 0px));
        padding: 1.25rem 1.5rem;
      }

      .checkout-addons-toast__heading {
        font-size: 1.375rem;
      }

      .checkout-addons-toast__product {
        font-size: 1.125rem;
      }

      .checkout-addons-toast__icon {
        width: 42px;
        height: 42px;
      }
    }
  `;
  document.head.appendChild(style);
}
